// StagingInterface.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import useLoadGoogleMaps from "./useLoadGoogleMaps";
import Papa from "papaparse";
import axios from "axios";
import "./StagingInterface.css";
import countries from "i18n-iso-countries";

const LIBRARIES = [];
const LS_KEY = "staged_points_v1";
const mapContainerStyle = { width: "100%", height: "100%" };

// Build full country list from i18n-iso-countries (English names)
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
const COUNTRY_MAP = countries.getNames("en", { select: "official" }); // { US: 'United States', ... }
const COUNTRIES = Object.entries(COUNTRY_MAP).map(([code, name]) => ({ code, name })).concat([{ code: "N/A", name: "N/A" }]);

const SUBCATEGORIES = [
  "AI",
  "Backup",
  "BTSUIT",
  "CHNGTORET",
  "Cloud",
  "Computing",
  "Data Storage",
  "IT Services",
  "Mining",
  "Self-build AI",
  "Self-build Content",
  "Self-build Crypto",
  "Self-build Financial/Institutional",
  "Self-build HPC",
  "Self-build Network & Telco",
  "Self-build Other",
  "Self-build Public Cloud",
  "Self-build Public Sector",
  "Self-build Social Media",
  "Self-build Systems Integrator/Managed Service Providers",
  "Telecom",
  "Telecommunications",
  "Trading",
  "WHLSALE",
];

// Normalise raw geocode output to an ISO code present in COUNTRIES. Returns ISO code (e.g., 'GB') or null.
const findCountryOption = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  // If it's already a 2-letter code
  if (/^[A-Za-z]{2}$/.test(s)) {
    const code = s.toUpperCase();
    if (COUNTRIES.find((c) => c.code === code)) return code;
  }
  // Try match by name (case-insensitive)
  const lowered = s.toLowerCase();
  const byName = COUNTRIES.find((c) => c.name.toLowerCase() === lowered);
  if (byName) return byName.code;
  // Try contains
  const contains = COUNTRIES.find((c) => lowered.includes(c.name.toLowerCase()));
  if (contains) return contains.code;
  return null;
};

const createApiInstance = (token, backendKey) => {
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(backendKey ? { "x-api-key": backendKey } : {}),
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
};

// ---------- Validation (as per CSV uploader) ----------
const validateRow = (row) => {
  const errors = [];
  const numericFields = ["size_l", "size_u", "size_d"];
  const requiredFields = ["sitename", "company", "category", "country"];

  requiredFields.forEach((f) => {
    if (row[f] == null || String(row[f]).trim() === "") errors.push(`Missing value for "${f}"`);
  });

  const lon = Number(row.longitude);
  const lat = Number(row.latitude);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) errors.push(`"longitude" and "latitude" must be valid numbers`);

  numericFields.forEach((f) => {
    if (row[f] != null && String(row[f]).trim() !== "" && isNaN(parseFloat(row[f]))) {
      errors.push(`"${f}" must be a valid number`);
    }
  });

  // confidence: blank ⇒ 0; if present must be 1,2,3
  if (row.confidence != null && String(row.confidence).trim() !== "") {
    const c = Number.parseInt(row.confidence, 10);
    if (![1, 2, 3].includes(c)) errors.push(`"confidence" must be 1, 2, or 3 (leave blank to default to 0)`);
  }
  return errors;
};

// ---------- Validation for landing stations ----------
const validateStation = (row) => {
  const errors = [];
  if (row.name == null || String(row.name).trim() === "") errors.push('Missing value for "name"');
  else if (String(row.name).length > 255) errors.push('"name" must be 255 characters or less');

  const lon = Number(row.longitude);
  const lat = Number(row.latitude);

  if (row.longitude == null || row.longitude === "" || !Number.isFinite(lon)) {
    errors.push('"longitude" is required and must be a valid number');
  } else if (lon < -180 || lon > 180) {
    errors.push('"longitude" out of range (-180 to 180)');
  }

  if (row.latitude == null || row.latitude === "" || !Number.isFinite(lat)) {
    errors.push('"latitude" is required and must be a valid number');
  } else if (lat < -90 || lat > 90) {
    errors.push('"latitude" out of range (-90 to 90)');
  }

  return errors;
};

// ---------- To /api/upload-csv payload ----------
const normaliseForSubmit = (row) => {
  const payload = {
    size_l: row.size_l !== "" && row.size_l != null ? parseFloat(row.size_l) : null,
    size_u: row.size_u !== "" && row.size_u != null ? parseFloat(row.size_u) : null,
    size_d: row.size_d !== "" && row.size_d != null ? parseFloat(row.size_d) : null,
    sitename: row.sitename?.trim() ?? null,
    company: row.company?.trim() ?? null,
    category: row.category?.trim() ?? null,
    subcategory: row.subcategory?.trim() ?? null,
    country: (row.country && row.country !== "N/A") ? (COUNTRY_MAP[row.country] ?? String(row.country).trim()) : null,
    coordinates: JSON.stringify([Number(row.longitude), Number(row.latitude)]), // [lon, lat]
  };
  if (row.confidence != null && String(row.confidence).trim() !== "") {
    payload.confidence = Number.parseInt(row.confidence, 10); // 1|2|3
  }
  return payload;
};

// ---------- Normalize landing station for API submission ----------
const normaliseStationForSubmit = (row) => {
  const lon = row.longitude !== null && row.longitude !== undefined && row.longitude !== ""
    ? Number(row.longitude)
    : null;
  const lat = row.latitude !== null && row.latitude !== undefined && row.latitude !== ""
    ? Number(row.latitude)
    : null;

  console.debug("normaliseStationForSubmit", {
    input: { name: row.name, longitude: row.longitude, latitude: row.latitude },
    output: { name: row.name?.trim() ?? null, longitude: lon, latitude: lat }
  });

  return {
    name: row.name?.trim() ?? null,
    longitude: lon,
    latitude: lat,
  };
};

const parseCoordinates = (row) => {
  if (row.coordinates && String(row.coordinates).trim() !== "") {
    try {
      const [lon, lat] = JSON.parse(row.coordinates);
      return { longitude: Number(lon), latitude: Number(lat) };
    } catch { /* fall back */ }
  }
  return { longitude: Number(row.longitude), latitude: Number(row.latitude) };
};

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const StagingInterface = ({ accessToken }) => {
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const BACKEND_KEY = process.env.REACT_APP_BACKEND_KEY;
  const api = useMemo(() => createApiInstance(accessToken, BACKEND_KEY), [accessToken, BACKEND_KEY]);

  // Existing data centres (read-only) and locally-staged candidates (editable)
  const [existing, setExisting] = useState([]);
  const [staged, setStaged] = useState([]);

  const [selected, setSelected] = useState(null);      // selected staged record id
  const [selectedStaged, setSelectedStaged] = useState(null); // left-click selected staged id (read-only view)
  const [selectedCentre, setSelectedCentre] = useState(null); // selected existing centre (read-only)
  const [editingCentre, setEditingCentre] = useState(null); // existing centre being edited (via right-click)
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState([]);
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);

  // World view by default
  const [userCenter, setUserCenter] = useState({ lat: 20, lng: 0 });
  const [zoom] = useState(2.5);

  const fileRef = useRef(null);

  // View type: 'datacenters' or 'landingstations'
  const [viewType, setViewType] = useState("datacenters");

  // Landing stations state (parallel to data centers)
  const [existingStations, setExistingStations] = useState([]);
  const [stagedStations, setStagedStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedStagedStation, setSelectedStagedStation] = useState(null);
  const [editingStation, setEditingStation] = useState(null);

  const LS_KEY_STATIONS = "staged_landing_stations_v1";
  const [creating, setCreating] = useState(null); // { lat, lng } when user right-clicks to create
  const rightClickTimerRef = useRef(null);
  const DOUBLE_RIGHT_MS = 300; // threshold to detect double right-click
  const lastRightClickRef = useRef(0);
  const suppressUntilRef = useRef(0);
  const isInitialMountRef = useRef(true);
  const [storageLoaded, setStorageLoaded] = useState(false);

  // Load existing data centres initially (visible on map)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/data-centres", { headers: { "x-api-key": BACKEND_KEY } });
        if (!alive) return;
        const data = (res.data || []).map((c) => ({
          ...c,
          longitude: Number(c.longitude),
          latitude: Number(c.latitude),
        }));
        setExisting(data);
      } catch (err) {
        console.error("Error fetching data centres:", err);
      }
    })();
    return () => { alive = false; };
  }, [api, BACKEND_KEY]);

  // Load existing landing stations when view switches
  useEffect(() => {
    if (viewType !== "landingstations") return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/landing-stations", { headers: { "x-api-key": BACKEND_KEY } });
        if (!alive) return;
        const data = (res.data || []).map((s) => ({
          ...s,
          longitude: Number(s.longitude),
          latitude: Number(s.latitude),
        }));
        setExistingStations(data);
      } catch (err) {
        console.error("Error fetching landing stations:", err);
      }
    })();
    return () => { alive = false; };
  }, [api, BACKEND_KEY, viewType]);

  // Load staged from localStorage based on view type
  useEffect(() => {
    try {
      const dcRaw = localStorage.getItem(LS_KEY);
      const lsRaw = localStorage.getItem(LS_KEY_STATIONS);
      let dcSaved = [];
      let lsSaved = [];
      let dcParseError = false;
      let lsParseError = false;
      try {
        dcSaved = JSON.parse(dcRaw || "[]");
      } catch (err) {
        dcParseError = true;
        console.warn('Failed to parse', LS_KEY, err);
      }
      try {
        lsSaved = JSON.parse(lsRaw || "[]");
      } catch (err) {
        lsParseError = true;
        console.warn('Failed to parse', LS_KEY_STATIONS, err);
      }
      if (Array.isArray(dcSaved) && dcSaved.length > 0) setStaged(dcSaved);
      if (Array.isArray(lsSaved) && lsSaved.length > 0) setStagedStations(lsSaved);
      // Mark initial mount complete AFTER a small delay to handle Strict Mode double-mount
      setTimeout(() => {
        isInitialMountRef.current = false;
        setStorageLoaded(true);
      }, 100);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    // Skip save on initial mount to prevent overwriting localStorage before load completes
    if (isInitialMountRef.current) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(staged));
    } catch (err) {
      console.warn('Failed to save staged to localStorage', err);
    }
  }, [staged]);

  useEffect(() => {
    // Skip save on initial mount to prevent overwriting localStorage before load completes
    if (isInitialMountRef.current) return;
    try {
      localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(stagedStations));
    } catch (err) {
      console.warn('Failed to save stagedStations to localStorage', err);
    }
  }, [stagedStations]);

  // Geolocation (re-centre; keep wide zoom)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // ---- CSV load (client-side) ----
  const resetFile = () => { if (fileRef.current) fileRef.current.value = ""; };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = results.data.map((r) => {
          const { id, geom, ...rest } = r;
          const { longitude, latitude } = parseCoordinates(rest);
          return {
            staging_id: genId(),
            sitename: rest.sitename ?? "",
            company: rest.company ?? "",
            category: rest.category ?? "",
            subcategory: rest.subcategory ?? "",
            country: rest.country ?? "",
            size_l: rest.size_l ?? "",
            size_u: rest.size_u ?? "",
            size_d: rest.size_d ?? "",
            confidence: rest.confidence ?? "",
            longitude,
            latitude,
          };
        });

        const allErrors = rows.flatMap((row, i) => validateRow(row).map((m) => `Row ${i + 1}: ${m}`));
        if (allErrors.length > 0) {
          setErrors(allErrors);
          setStatus("Validation errors found. The file has been cleared.");
          resetFile();
          return;
        }

        setStaged((prev) => {
          const next = [...rows, ...prev];
          try { localStorage.setItem(LS_KEY, JSON.stringify(next)); console.debug('Immediate save after CSV load', LS_KEY, next.length); } catch (err) { console.warn('Immediate localStorage save failed', err); }
          return next;
        });
        setErrors([]);
        setStatus(`Loaded ${rows.length} staged point(s).`);
        resetFile();
      },
      error: (err) => {
        setErrors([`CSV parsing error: ${err.message}`]);
        setStatus(`CSV parsing error; the file has been cleared.`);
        resetFile();
      },
    });
  };

  // ---- selection & editing (staged) ----
  const selectRecord = (rec) => {
    setSelected(rec.staging_id);
    setSelectedCentre(null);
    setErrors([]);
    setStatus("");
    setForm({ ...rec });
  };
  const deselect = () => { setSelected(null); setForm({}); setErrors([]); setStatus(""); };
  const deselectAll = () => { setSelected(null); setSelectedStaged(null); setForm({}); setErrors([]); setStatus(""); };
  const startEditCentre = (centre) => {
    // Populate form with centre data (convert country to ISO if possible)
    const countryCode = findCountryOption(centre.country) ?? (centre.country ? "N/A" : "");
    setEditingCentre(centre);
    setSelected(null);
    setSelectedCentre(null);
    setErrors([]);
    setStatus("");
    setForm({
      id: centre.id,
      sitename: centre.sitename ?? centre.name ?? "",
      company: centre.company ?? "",
      category: centre.category ?? "",
      subcategory: centre.subcategory ?? "",
      country: countryCode,
      size_l: centre.size_l ?? "",
      size_u: centre.size_u ?? "",
      size_d: centre.size_d ?? "",
      confidence: centre.confidence ?? "",
      longitude: Number(centre.longitude),
      latitude: Number(centre.latitude),
    });
  };
  const cancelEditCentre = () => { setEditingCentre(null); setForm({}); setErrors([]); setStatus(""); };
  const saveEditedCentre = async () => {
    if (!editingCentre) return;
    const rowErrors = validateRow(form);
    setErrors(rowErrors);
    if (rowErrors.length > 0) { setStatus("Validation errors; unable to save."); return; }
    try {
      setSending(true);
      // Build payload matching backend PUT /data-centres/:id expectations
      const payload = {
        sitename: form.sitename?.trim() ?? null,
        company: form.company?.trim() ?? null,
        category: form.category?.trim() ?? null,
        subcategory: form.subcategory?.trim() ?? null,
        // Send full country name (or null) — map ISO code to name via COUNTRY_MAP
        country: (form.country && form.country !== "N/A") ? (COUNTRY_MAP[form.country] ?? String(form.country).trim()) : null,
        size_l: form.size_l !== "" && form.size_l != null ? parseFloat(form.size_l) : null,
        size_u: form.size_u !== "" && form.size_u != null ? parseFloat(form.size_u) : null,
        size_d: form.size_d !== "" && form.size_d != null ? parseFloat(form.size_d) : null,
        // Backend validation expects confidence to be 1,2,3 or blank — send blank when empty
        confidence: form.confidence != null && String(form.confidence).trim() !== "" ? Number.parseInt(form.confidence, 10) : "",
        longitude: Number(form.longitude),
        latitude: Number(form.latitude),
      };
      // Attempt update via PUT
      const res = await api.put(`/data-centres/${editingCentre.id}`, payload, { headers: { "x-api-key": BACKEND_KEY } });
      // Refresh existing list
      try {
        const listRes = await api.get("/data-centres", { headers: { "x-api-key": BACKEND_KEY } });
        const data = (listRes.data || []).map((c) => ({ ...c, longitude: Number(c.longitude), latitude: Number(c.latitude) }));
        setExisting(data);
      } catch (fetchErr) {
        console.warn("Failed to refresh data centres after edit:", fetchErr);
        // Optimistic merge using returned data if available or the form
        const maybeUpdated = res?.data || null;
        const updated = maybeUpdated ? {
          ...maybeUpdated,
          longitude: Number(maybeUpdated.longitude ?? payload.longitude),
          latitude: Number(maybeUpdated.latitude ?? payload.latitude),
        } : {
          id: editingCentre.id,
          sitename: payload.sitename,
          company: payload.company,
          category: payload.category,
          subcategory: payload.subcategory,
          country: payload.country,
          size_l: payload.size_l,
          size_u: payload.size_u,
          size_d: payload.size_d,
          confidence: payload.confidence,
          longitude: payload.longitude,
          latitude: payload.latitude,
        };
        setExisting((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      }
      setStatus("Updated data centre.");
      setEditingCentre(null);
      setForm({});
      setErrors([]);
      setSending(false);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data || err.message;
      setStatus(`Save failed: ${serverMsg}`);
      setSending(false);
    }
  };
  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (viewType === "landingstations") {
      setStagedStations((prev) => {
        const next = prev.map((r) => (r.staging_id === form.staging_id ? { ...r, [field]: value } : r));
        try { localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
    } else {
      setStaged((prev) => {
        const next = prev.map((r) => (r.staging_id === form.staging_id ? { ...r, [field]: value } : r));
        try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
    }
  };
  const handleMarkerDrag = (staging_id, e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setStaged((prev) => {
      const next = prev.map((r) => (r.staging_id === staging_id ? { ...r, latitude: newLat, longitude: newLng } : r));
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
      return next;
    });
    if (selected === staging_id || selectedStaged === staging_id) setForm((p) => ({ ...p, latitude: newLat, longitude: newLng }));
  };

  // ---- Landing Station handlers (parallel to data center handlers) ----
  const selectRecordStation = (rec) => {
    setSelectedStation(rec.staging_id);
    setSelectedCentre(null);
    setSelected(null);
    setErrors([]);
    setStatus("");
    setForm({ ...rec });
  };

  const startEditStation = (station) => {
    setEditingStation(station);
    setSelectedStation(null);
    setSelectedCentre(null);
    setSelected(null);
    setErrors([]);
    setStatus("");
    setForm({
      id: station.id,
      name: station.name ?? "",
      latitude: Number(station.latitude),
      longitude: Number(station.longitude),
    });
  };

  const cancelEditStation = () => { setEditingStation(null); setForm({}); setErrors([]); setStatus(""); };

  const saveEditedStation = async () => {
    if (!editingStation) return;
    const errors = [];
    if (!form.name || String(form.name).trim() === "") errors.push('Missing value for "name"');
    const lon = Number(form.longitude);
    const lat = Number(form.latitude);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) errors.push('"longitude" and "latitude" must be valid numbers');
    else {
      if (lon < -180 || lon > 180) errors.push('"longitude" out of range (-180 to 180)');
      if (lat < -90 || lat > 90) errors.push('"latitude" out of range (-90 to 90)');
    }

    setErrors(errors);
    if (errors.length > 0) { setStatus("Validation errors; unable to save."); return; }

    try {
      setSending(true);
      const payload = {
        name: form.name?.trim() ?? null,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
      };

      const res = await api.put(`/landing-stations/${editingStation.id}`, payload, { headers: { "x-api-key": BACKEND_KEY } });

      try {
        const listRes = await api.get("/landing-stations", { headers: { "x-api-key": BACKEND_KEY } });
        const data = (listRes.data || []).map((s) => ({ ...s, longitude: Number(s.longitude), latitude: Number(s.latitude) }));
        setExistingStations(data);
      } catch (fetchErr) {
        console.warn("Failed to refresh landing stations after edit:", fetchErr);
        const maybeUpdated = res?.data || payload;
        const updated = {
          id: editingStation.id,
          name: maybeUpdated.name || payload.name,
          longitude: Number(maybeUpdated.longitude ?? payload.longitude),
          latitude: Number(maybeUpdated.latitude ?? payload.latitude),
        };
        setExistingStations((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      }

      setStatus("Updated landing station.");
      setEditingStation(null);
      setForm({});
      setErrors([]);
      setSending(false);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data || err.message;
      setStatus(`Save failed: ${serverMsg}`);
      setSending(false);
    }
  };

  const handleMarkerDragStation = (staging_id, e) => {
    const newLat = e.latLng.lat();
    const newLng = e.latLng.lng();
    setStagedStations((prev) => {
      const next = prev.map((r) => (r.staging_id === staging_id ? { ...r, latitude: newLat, longitude: newLng } : r));
      try { localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
      return next;
    });
    if (selectedStation === staging_id || selectedStagedStation === staging_id) {
      setForm((p) => ({ ...p, latitude: newLat, longitude: newLng }));
    }
  };

  // Ensure we flush staged state to localStorage if the user refreshes or closes the tab
  useEffect(() => {
    const flush = () => {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify(staged));
        localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(stagedStations));
      } catch (err) {
        console.warn('Failed to flush staged to localStorage', err);
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [staged, stagedStations]);

  // Reverse geocode lat/lng to a country name (best-effort)
  const reverseGeocodeCountry = async (lat, lng) => {
    const latNum = Number(lat);
    const lngNum = Number(lng);
    console.debug("reverseGeocodeCountry called", { lat: latNum, lng: lngNum });
    const latlng = { lat: latNum, lng: lngNum };

    const tryJsGeocoder = async () => {
      console.debug("reverseGeocodeCountry: trying JS geocoder");
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) return null;
      const geocoder = new window.google.maps.Geocoder();
      try {
        const results = await new Promise((resolve, reject) => {
          geocoder.geocode({ location: latlng }, (res, status) => {
            if (status === "OK") resolve(res);
            else reject(new Error(status));
          });
        });
        if (!Array.isArray(results)) return null;
        for (const r of results) {
          if (!r.address_components) continue;
          const countryComp = r.address_components.find((c) => (c.types || []).includes("country"));
          if (countryComp && countryComp.long_name) return countryComp.long_name;
        }
      } catch (err) {
        console.warn("reverseGeocodeCountry JS geocoder failed:", err.message || err);
        return null;
      }
      return null;
    };

    const tryHttpGeocode = async () => {
      console.debug("reverseGeocodeCountry: trying HTTP geocode fallback");
      const key = API_KEY || process.env.REACT_APP_GOOGLE_API_KEY;
      if (!key) return null;
      try {
        const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latNum},${lngNum}&key=${encodeURIComponent(key)}`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const body = await res.json();
        if (!Array.isArray(body.results)) return null;
        for (const r of body.results) {
          if (!r.address_components) continue;
          const countryComp = r.address_components.find((c) => (c.types || []).includes("country"));
          if (countryComp && countryComp.long_name) return countryComp.long_name;
        }
      } catch (err) {
        console.warn("reverseGeocodeCountry HTTP geocode failed:", err.message || err);
      }
      return null;
    };
    const tryNominatim = async () => {
      console.debug("reverseGeocodeCountry: trying Nominatim fallback");
      try {
        // Nominatim reverse geocode (OpenStreetMap) - lightweight fallback for dev
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latNum}&lon=${lngNum}`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return null;
        const body = await res.json();
        const country = body?.address?.country;
        if (country) return country;
        // try country_code if available
        const code = body?.address?.country_code;
        if (code) return code.toUpperCase();
      } catch (err) {
        console.warn("reverseGeocodeCountry Nominatim fallback failed:", err.message || err);
      }
      return null;
    };

    // Try the JS geocoder first, then fall back to HTTP and finally Nominatim
    const jsResult = await tryJsGeocoder();
    if (jsResult) return jsResult;
    const httpResult = await tryHttpGeocode();
    if (httpResult) return httpResult;
    return await tryNominatim();
  };
  // ---- Right-click create flow ----
  const handleMapRightClick = (e) => {
    const now = Date.now();
    // If we're in a suppression window from a detected double-right-click, ignore.
    if (suppressUntilRef.current && now < suppressUntilRef.current) {
      console.debug("handleMapRightClick: suppressed by recent double-right-click");
      return;
    }

    const last = lastRightClickRef.current || 0;
    // If second click occurs within DOUBLE_RIGHT_MS, treat as a double-right-click: cancel pending single action and suppress further opens briefly.
    if (last && (now - last) <= DOUBLE_RIGHT_MS) {
      if (rightClickTimerRef.current) {
        clearTimeout(rightClickTimerRef.current);
        rightClickTimerRef.current = null;
      }
      // Suppress any further form opens for a short window to ensure the form doesn't appear at all
      suppressUntilRef.current = now + DOUBLE_RIGHT_MS;
      lastRightClickRef.current = 0;
      console.debug("handleMapRightClick: detected double right-click; suppressing form open");
      return;
    }

    // Otherwise, schedule single-right-click action after threshold; it will be cancelled if a second right-click occurs soon.
    lastRightClickRef.current = now;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    rightClickTimerRef.current = setTimeout(() => {
      // Clear last click timestamp once we've committed to single-click action
      lastRightClickRef.current = 0;
      rightClickTimerRef.current = null;
      console.debug("handleMapRightClick (single)", { lat, lng, mapsLoaded });
      setCreating({ latitude: lat, longitude: lng });
      setSelected(null);
      setSelectedCentre(null);
      setSelectedStation(null);
      setSelectedStagedStation(null);
      setErrors([]);
      setStatus("");

      // Build form based on view type
      if (viewType === "landingstations") {
        setForm({
          staging_id: null,
          name: "",
          latitude: lat,
          longitude: lng,
        });
      } else {
        setForm({
          staging_id: null,
          sitename: "",
          company: "",
          category: "",
          subcategory: "",
          country: "",
          size_l: "",
          size_u: "",
          size_d: "",
          confidence: "",
          latitude: lat,
          longitude: lng,
        });
      }

      // Best-effort: reverse geocode to pre-fill country (only for data centers)
      if (viewType === "datacenters" && mapsLoaded) {
        (async () => {
          const countryRaw = await reverseGeocodeCountry(lat, lng);
          const countryCode = findCountryOption(countryRaw) ?? (countryRaw === "N/A" ? "N/A" : null);
          setForm((p) => ({ ...p, country: countryCode ?? "N/A" }));
        })();
      } else {
        console.debug("Queueing pendingCountryLookup", { lat, lng });
        setPendingCountryLookup({ latitude: lat, longitude: lng });
      }
    }, DOUBLE_RIGHT_MS);
  };

  const cancelCreate = () => { setCreating(null); deselect(); };

  const finishCreate = () => {
    // validate form based on view type
    const rowErrors = viewType === "landingstations" ? validateStation(form) : validateRow(form);
    if (rowErrors.length > 0) { setErrors(rowErrors); setStatus("Validation errors; cannot stage."); return; }
    const newRec = { ...form, staging_id: genId() };
    if (viewType === "landingstations") {
      setStagedStations((prev) => {
        const next = [newRec, ...prev];
        try { localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
      setStatus("Staged new landing station (local)");
    } else {
      setStaged((prev) => {
        const next = [newRec, ...prev];
        try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
      setStatus("Staged new data centre (local)");
    }
    setCreating(null);
    setErrors([]);
    setForm({});
  };

  // ---- Stage & Admit: validate, send directly to backend, refresh existing list ----
  const handleStageAndAdmit = async () => {
    if (viewType === "landingstations") {
      const rowErrors = validateStation(form);
      setErrors(rowErrors);
      if (rowErrors.length > 0) { setStatus("Validation errors; unable to admit."); return; }
      try {
        setSending(true);
        const record = normaliseStationForSubmit(form);
        const res = await api.post("/landing-stations", record, { headers: { "x-api-key": BACKEND_KEY } });
        // Refresh existing list
        try {
          const listRes = await api.get("/landing-stations", { headers: { "x-api-key": BACKEND_KEY } });
          const data = (listRes.data || []).map((s) => ({ ...s, longitude: Number(s.longitude), latitude: Number(s.latitude) }));
          setExistingStations(data);
        } catch (fetchErr) {
          console.warn("Failed to refresh landing stations after stage & admit:", fetchErr);
          const inserted = res?.data || { id: genId(), ...record };
          setExistingStations((prev) => [inserted, ...prev]);
        }
        setStatus("Admitted and inserted into database.");
        setCreating(null);
        setForm({});
        setErrors([]);
        setSending(false);
      } catch (err) {
        const serverMsg = err.response?.data?.errors?.join(", ") || err.response?.data?.message || err.response?.data || err.message;
        setStatus(`Admit failed: ${serverMsg}`);
        setSending(false);
      }
      return;
    }

    const rowErrors = validateRow(form);
    setErrors(rowErrors);
    if (rowErrors.length > 0) { setStatus("Validation errors; unable to admit."); return; }
    try {
      setSending(true);
      const record = normaliseForSubmit(form);
      const res = await api.post("/api/upload-csv", { data: [record] }, { headers: { "x-api-key": BACKEND_KEY } });
      // Don't add to staged; refresh existing list
      try {
        const listRes = await api.get("/data-centres", { headers: { "x-api-key": BACKEND_KEY } });
        const data = (listRes.data || []).map((c) => ({
          ...c,
          longitude: Number(c.longitude),
          latitude: Number(c.latitude),
        }));
        setExisting(data);
      } catch (fetchErr) {
        console.warn("Failed to refresh data centres after stage & admit:", fetchErr);
        const maybeCreated = res?.data?.[0] || null;
        const inserted = maybeCreated ? {
          ...maybeCreated,
          longitude: Number(maybeCreated.longitude || record.coordinates && JSON.parse(record.coordinates)[0]),
          latitude: Number(maybeCreated.latitude || record.coordinates && JSON.parse(record.coordinates)[1]),
        } : {
          id: genId(),
          sitename: record.sitename,
          company: record.company,
          category: record.category,
          subcategory: record.subcategory,
          country: record.country,
          size_l: record.size_l,
          size_u: record.size_u,
          size_d: record.size_d,
          confidence: record.confidence,
          longitude: JSON.parse(record.coordinates)[0],
          latitude: JSON.parse(record.coordinates)[1],
        };
        setExisting((prev) => [inserted, ...prev]);
      }

      setStatus("Admitted and inserted into database.");
      setCreating(null);
      setForm({});
      setErrors([]);
      setSending(false);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data || err.message;
      setStatus(`Admit failed: ${serverMsg}`);
      setSending(false);
    }
  };

  // ---- Admit (only backend call) ----
  const handleAdmit = async () => {
    if (viewType === "landingstations") {
      const rowErrors = validateStation(form);
      setErrors(rowErrors);
      if (rowErrors.length > 0) { setStatus("Validation errors; unable to admit."); return; }
      try {
        setSending(true);
        const record = normaliseStationForSubmit(form);
        const res = await api.post("/landing-stations", record, { headers: { "x-api-key": BACKEND_KEY } });
        // Remove from staged (and persist immediately)
        setStagedStations((prev) => {
          const next = prev.filter((r) => r.staging_id !== form.staging_id);
          try { localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
          return next;
        });
        // Refresh existing list
        try {
          const listRes = await api.get("/landing-stations", { headers: { "x-api-key": BACKEND_KEY } });
          const data = (listRes.data || []).map((s) => ({ ...s, longitude: Number(s.longitude), latitude: Number(s.latitude) }));
          setExistingStations(data);
        } catch (fetchErr) {
          console.warn("Failed to refresh landing stations after admit:", fetchErr);
          const inserted = res?.data || { id: genId(), ...record };
          setExistingStations((prev) => [inserted, ...prev]);
        }
        setStatus("Admitted and inserted into database.");
        setSelectedStation(null);
        setSelectedStagedStation(null);
        setForm({});
        setErrors([]);
        setSending(false);
      } catch (err) {
        const serverMsg = err.response?.data?.errors?.join(", ") || err.response?.data?.message || err.response?.data || err.message;
        setStatus(`Admit failed: ${serverMsg}`);
        setSending(false);
      }
      return;
    }

    const rowErrors = validateRow(form);
    setErrors(rowErrors);
    if (rowErrors.length > 0) { setStatus("Validation errors; unable to admit."); return; }
    try {
      setSending(true);
      const record = normaliseForSubmit(form);
      const res = await api.post("/api/upload-csv", { data: [record] }, { headers: { "x-api-key": BACKEND_KEY } });
      // Remove from staged regardless
      setStaged((prev) => {
        const next = prev.filter((r) => r.staging_id !== form.staging_id);
        try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });

      // Try to refresh existing centres from server so the newly admitted centre appears immediately
      try {
        const listRes = await api.get("/data-centres", { headers: { "x-api-key": BACKEND_KEY } });
        const data = (listRes.data || []).map((c) => ({
          ...c,
          longitude: Number(c.longitude),
          latitude: Number(c.latitude),
        }));
        setExisting(data);
      } catch (fetchErr) {
        // If refreshing failed, do an optimistic insert using the normalized record and server response if available
        console.warn("Failed to refresh data centres after admit:", fetchErr);
        const maybeCreated = res?.data?.[0] || null;
        const inserted = maybeCreated ? {
          ...maybeCreated,
          longitude: Number(maybeCreated.longitude || record.coordinates && JSON.parse(record.coordinates)[0]),
          latitude: Number(maybeCreated.latitude || record.coordinates && JSON.parse(record.coordinates)[1]),
        } : {
          id: genId(),
          sitename: record.sitename,
          company: record.company,
          category: record.category,
          subcategory: record.subcategory,
          country: record.country,
          size_l: record.size_l,
          size_u: record.size_u,
          size_d: record.size_d,
          confidence: record.confidence,
          longitude: JSON.parse(record.coordinates)[0],
          latitude: JSON.parse(record.coordinates)[1],
        };
        setExisting((prev) => [inserted, ...prev]);
      }

      setStatus("Admitted and inserted into database.");
      deselect();
      setSending(false);
    } catch (err) {
      const serverMsg = err.response?.data?.message || err.response?.data || err.message;
      setStatus(`Admit failed: ${serverMsg}`);
      setSending(false);
    }
  };

  // ---- Reject (discard locally) ----
  const handleReject = () => {
    if (!form?.staging_id) return;
    if (viewType === "landingstations") {
      setStagedStations((prev) => {
        const next = prev.filter((r) => r.staging_id !== form.staging_id);
        try { localStorage.setItem(LS_KEY_STATIONS, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
      setStatus("Rejected and discarded from staging.");
      setSelectedStation(null);
      setSelectedStagedStation(null);
      setForm({});
    } else {
      setStaged((prev) => {
        const next = prev.filter((r) => r.staging_id !== form.staging_id);
        try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) { console.warn('Immediate localStorage save failed', err); }
        return next;
      });
      setStatus("Rejected and discarded from staging.");
      deselect();
    }
  };

  // ---- Existing marker icons (keep original) ----
  const existingIcon = (cat) => {
    const url = "icons/dc_existing.png";
    const maps = window.google?.maps;
    if (!maps) return { url };
    const SizeCtor = maps.Size;
    if (typeof SizeCtor === "function") {
      try {
        return { url, scaledSize: new SizeCtor(20, 20) };
      } catch (err) {
        return { url };
      }
    }
    return { url };
  };

  const { loaded: mapsLoaded, error: mapsError } = useLoadGoogleMaps({ apiKey: API_KEY, libraries: LIBRARIES });
  const [pendingCountryLookup, setPendingCountryLookup] = useState(null);
  useEffect(() => {
    return () => {
      if (rightClickTimerRef.current) {
        clearTimeout(rightClickTimerRef.current);
        rightClickTimerRef.current = null;
      }
      lastRightClickRef.current = 0;
      suppressUntilRef.current = 0;
    };
  }, []);
  useEffect(() => {
    if (!mapsLoaded || !pendingCountryLookup) return;
    console.debug("Running queued pendingCountryLookup", pendingCountryLookup);
    let alive = true;
    (async () => {
      try {
        const { latitude, longitude } = pendingCountryLookup;
        const countryRaw = await reverseGeocodeCountry(latitude, longitude);
        const countryCode = findCountryOption(countryRaw) ?? (countryRaw === "N/A" ? "N/A" : null);
        console.debug("Queued lookup result", { countryRaw, countryCode });
        if (alive) setForm((p) => ({ ...p, country: countryCode ?? "N/A" }));
      } catch (err) {
        console.warn("Queued pendingCountryLookup failed", err);
      } finally {
        if (alive) setPendingCountryLookup(null);
      }
    })();
    return () => { alive = false; };
  }, [mapsLoaded, pendingCountryLookup]);

  return (
    <div style={{ width: "100%", height: "100vh" }}>
      <a href="/" className="btn btn--ghost home-fab" aria-label="Go to Home">← Home</a>
      <a href="/staging_powerplants" className="btn btn--ghost powerplant-fab" aria-label="Go to Power Plants Staging"> Power Plants Staging →</a>

      {/* Toolbar with back/home, centred file input, and actions/legend */}
      <div className="staging-toolbar">
        <div className="staging-toolbar__left">
          {/* View Type Dropdown */}
          <label style={{ position: "fixed", top: "125px", fontWeight: "500" }}>
            <select
              value={viewType}
              onChange={(e) => {
                setViewType(e.target.value);
                // Clear selections when switching views
                deselectAll();
                setSelectedStation(null);
                setSelectedStagedStation(null);
                setEditingStation(null);
                setEditingCentre(null);
              }}
              style={{
                padding: "6px",
                borderRadius: "4px",
                border: "1px solid #ccc",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              <option value="datacenters">Data Centers</option>
              <option value="landingstations">Landing Stations</option>
            </select>
          </label>
        </div>
        <div className="staging-toolbar__center">
          <div style={{ display: 'flex', justifyContent: "stretch", alignItems: 'stretch', gap: 12 }}>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} />
            <div style={{ display: "flex", alignItems: "center", padding: "6px", borderRadius: "6px", fontSize: 13, color: '#444', backgroundColor: "#ffffffbf" }}>
              Staged: <strong>{viewType === 'landingstations' ? stagedStations.length : staged.length}</strong>
            </div>
          </div>
        </div>
        <div className="staging-toolbar__right">
          <button
            className="btn btn--ghost"
            onClick={() => {
              if (viewType === "landingstations") {
                setStagedStations([]);
                localStorage.removeItem(LS_KEY_STATIONS);
                setSelectedStation(null);
                setSelectedStagedStation(null);
              } else {
                setStaged([]);
                localStorage.removeItem(LS_KEY);
                deselect();
              }
            }}
          >
            Clear staged
          </button>
        </div>
      </div>

      {status && <div className="staging-status">{status}</div>}
      {errors.length > 0 && (
        <div className="staging-errors" role="region" aria-label="Validation errors">
          <strong>Validation errors:</strong>
          <ul style={{ margin: "4px 0 0 18px" }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {!mapsLoaded && !mapsError && (
        <div style={{ padding: 16 }}>Loading map...</div>
      )}
      {mapsError && (
        <div role="alert" style={{ padding: 16, color: "#b00020" }}>
          Error loading Google Maps: {mapsError.message}
        </div>
      )}

      {mapsLoaded && storageLoaded && (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={userCenter}
          zoom={zoom}
          // Intentionally do not deselect on map click so forms remain open until user cancels
          onClick={() => { /* no-op: keep forms open on outside click */ }}
          onRightClick={handleMapRightClick}
          onDblClick={() => {
            // Immediate suppression when the user double-clicks the map
            if (rightClickTimerRef.current) {
              clearTimeout(rightClickTimerRef.current);
              rightClickTimerRef.current = null;
            }
            suppressUntilRef.current = Date.now() + DOUBLE_RIGHT_MS;
            lastRightClickRef.current = 0;
            console.debug("onDblClick: cancelled pending right-click form and suppressed opens");
          }}
          onZoomChanged={() => {
            // If the map zooms (likely from a double-click), cancel any pending create form open and suppress briefly
            if (rightClickTimerRef.current) {
              clearTimeout(rightClickTimerRef.current);
              rightClickTimerRef.current = null;
            }
            suppressUntilRef.current = Date.now() + DOUBLE_RIGHT_MS;
            lastRightClickRef.current = 0;
            console.debug("onZoomChanged: cancelled pending right-click form and suppressed opens");
          }}
          options={{
            minZoom: 2,
            maxZoom: 24,
            restriction: { latLngBounds: { north: 85, south: -85, west: -180, east: 180 }, strictBounds: false },
          }}
        >
          {/* Existing entities (read-only; custom icons; open info on click) */}
          {viewType === "datacenters" ? (
            existing.map((c) => {
              const lat = Math.round(Number(c.latitude) * 1e6) / 1e6;
              const lng = Math.round(Number(c.longitude) * 1e6) / 1e6;
              return (
                <Marker
                  key={`dc-${c.id}`}
                  position={{ lat, lng }}
                  icon={existingIcon(c.category)}
                  onClick={() => { setSelectedCentre(c); setSelected(null); setSelectedStation(null); }}
                  onRightClick={() => startEditCentre(c)}
                  zIndex={0}
                />
              );
            })
          ) : (
            existingStations.map((s) => {
              const lat = Math.round(Number(s.latitude) * 1e6) / 1e6;
              const lng = Math.round(Number(s.longitude) * 1e6) / 1e6;
              const maps = window.google?.maps;
              return (
                <Marker
                  key={`ls-${s.id}`}
                  position={{ lat, lng }}
                  icon={{
                    url: "icons/structure.png",
                    scaledSize: maps?.Size ? new maps.Size(22, 22) : undefined,
                  }}
                  onClick={() => { setSelectedStation(s); setSelectedCentre(null); setSelected(null); }}
                  onRightClick={() => startEditStation(s)}
                  zIndex={0}
                />
              );
            })
          )}

          {/* Staged candidates (default Google markers, draggable) */}
          {viewType === "datacenters" ? staged.map((rec) => {
            const lat = Math.round(Number(rec.latitude) * 1e6) / 1e6;
            const lng = Math.round(Number(rec.longitude) * 1e6) / 1e6;
            return (
              <Marker
                key={`stage-${rec.staging_id}`}
                position={{ lat, lng }}
                // Left-click: show read-only details
                onClick={() => { setSelectedStaged(rec.staging_id); setSelected(null); setSelectedCentre(null); setForm({ ...rec }); setErrors([]); setStatus(""); }}
                // Right-click: open editable form
                onRightClick={() => { selectRecord(rec); setSelectedStaged(null); }}
                onDragEnd={async (e) => {
                  handleMarkerDrag(rec.staging_id, e);
                  const newLat = e.latLng.lat();
                  const newLng = e.latLng.lng();
                  try {
                    const countryRaw = await reverseGeocodeCountry(newLat, newLng);
                    const countryCode = findCountryOption(countryRaw) ?? (countryRaw === "N/A" ? "N/A" : null);
                    const finalCountry = countryCode ?? "N/A";
                    setStaged((prev) => prev.map((r) => (r.staging_id === rec.staging_id ? { ...r, country: finalCountry } : r)));
                    if (selected === rec.staging_id || selectedStaged === rec.staging_id) setForm((p) => ({ ...p, country: finalCountry }));
                  } catch (err) {
                    // ignore
                  }
                }}
                draggable
                zIndex={2}
                icon={{ url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png" }}
              />
            );
          }) : stagedStations.map((rec) => {
            const lat = Math.round(Number(rec.latitude) * 1e6) / 1e6;
            const lng = Math.round(Number(rec.longitude) * 1e6) / 1e6;
            return (
              <Marker
                key={`stage-station-${rec.staging_id}`}
                position={{ lat, lng }}
                onClick={() => { setSelectedStagedStation(rec.staging_id); setSelectedStation(null); setSelectedCentre(null); setForm({ ...rec }); setErrors([]); setStatus(""); }}
                onRightClick={() => { selectRecordStation(rec); setSelectedStagedStation(null); }}
                onDragEnd={async (e) => {
                  handleMarkerDragStation(rec.staging_id, e);
                  const newLat = e.latLng.lat();
                  const newLng = e.latLng.lng();
                  try {
                    const countryRaw = await reverseGeocodeCountry(newLat, newLng);
                    const countryCode = findCountryOption(countryRaw) ?? (countryRaw === "N/A" ? "N/A" : null);
                    const finalCountry = countryCode ?? "N/A";
                    setStagedStations((prev) => prev.map((r) => (r.staging_id === rec.staging_id ? { ...r, country: finalCountry } : r)));
                    if (selectedStation === rec.staging_id || selectedStagedStation === rec.staging_id) setForm((p) => ({ ...p, country: finalCountry }));
                  } catch (err) {
                    // ignore
                  }
                }}
                draggable
                zIndex={2}
                icon={{ url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png" }}
              />
            );
          })}

          {/* Read-only InfoWindow for staged points (left-click) */}
          {selectedStaged && (() => {
            const rec = staged.find((s) => s.staging_id === selectedStaged);
            if (!rec) return null;
            return (
              <InfoWindow
                position={{ lat: Number(rec.latitude), lng: Number(rec.longitude) }}
                onCloseClick={() => { setSelectedStaged(null); setForm({}); setErrors([]); setStatus(""); }}
                options={{ zIndex: 1000 }}
              >
                <div style={{ minWidth: 300 }}>
                  <h5 style={{ marginBottom: 8 }}>{rec.sitename || "Staged point"}</h5>
                  <ul>
                    {rec.sitename && <li><strong>Site Name:</strong> {rec.sitename}</li>}
                    {rec.company && <li><strong>Company:</strong> {rec.company}</li>}
                    {rec.category && <li><strong>Category:</strong> {rec.category}</li>}
                    {rec.country && <li><strong>Country:</strong> {COUNTRY_MAP[rec.country] ?? rec.country}</li>}
                    {rec.size_l && <li><strong>Size (Live):</strong> {`${rec.size_l} MW`}</li>}
                    {rec.size_u && <li><strong>Size (Under Construction):</strong> {`${rec.size_u} MW`}</li>}
                    {rec.size_d && <li><strong>Size (Design):</strong> {`${rec.size_d} MW`}</li>}
                  </ul>
                  <div style={{ marginTop: 8 }}>
                    <button className="btn btn--primary" onClick={() => { /* open editable form */ selectRecord(rec); setSelectedStaged(null); }}>Edit</button>
                    <button className="btn btn--secondary" onClick={() => { setForm({ ...rec }); handleAdmit(); }} style={{ marginLeft: 8 }}>Admit</button>
                    <button className="btn btn--secondary" onClick={() => { setForm({ ...rec }); handleReject(); }} style={{ marginLeft: 8 }}>Reject</button>
                  </div>
                </div>
              </InfoWindow>
            );
          })()}

          {/* Read-only InfoWindow for staged landing stations (left-click) */}
          {selectedStagedStation && (() => {
            const rec = stagedStations.find((s) => s.staging_id === selectedStagedStation);
            if (!rec) return null;
            return (
              <InfoWindow
                position={{ lat: Number(rec.latitude), lng: Number(rec.longitude) }}
                onCloseClick={() => { setSelectedStagedStation(null); setForm({}); setErrors([]); setStatus(""); }}
                options={{ zIndex: 1000 }}
              >
                <div style={{ minWidth: 300 }}>
                  <h5 style={{ marginBottom: 8 }}>{rec.name || "Staged landing station"}</h5>
                  <ul>
                    {rec.name && <li><strong>Name:</strong> {rec.name}</li>}
                    <li><strong>Latitude:</strong> {rec.latitude}</li>
                    <li><strong>Longitude:</strong> {rec.longitude}</li>
                  </ul>
                  <div style={{ marginTop: 8 }}>
                    <button className="btn btn--primary" onClick={() => { selectRecordStation(rec); setSelectedStagedStation(null); }}>Edit</button>
                    <button className="btn btn--secondary" onClick={() => { setForm({ ...rec }); handleAdmit(); }} style={{ marginLeft: 8 }}>Admit</button>
                    <button className="btn btn--secondary" onClick={() => { setForm({ ...rec }); handleReject(); }} style={{ marginLeft: 8 }}>Reject</button>
                  </div>
                </div>
              </InfoWindow>
            );
          })()}

          {/* InfoWindow shown while creating a new staged point (right-click) */}
          {creating && viewType === "datacenters" && (
            <InfoWindow
              position={{ lat: creating.latitude, lng: creating.longitude }}
              onCloseClick={cancelCreate}
              options={{ zIndex: 2000 }}
            >
              <div style={{ minWidth: 300 }}>
                <h5 style={{ marginBottom: 8 }}>Create staged data centre</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                    <span>Site name</span>
                    <input value={form.sitename ?? ""} onChange={(e) => handleChange("sitename", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                    <span>Company</span>
                    <input value={form.company ?? ""} onChange={(e) => handleChange("company", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Category</span>
                    <select value={form.category ?? ""} onChange={(e) => handleChange("category", e.target.value)}>
                      <option value="">-- Select --</option>
                      <option value="Colocation">Colocation</option>
                      <option value="Self-build">Self-build</option>
                    </select>
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Subcategory</span>
                    <select value={form.subcategory ?? ""} onChange={(e) => handleChange("subcategory", e.target.value)}>
                      <option value="">-- Select --</option>
                      {SUBCATEGORIES.map((sub, idx) => <option key={idx} value={sub}>{sub}</option>)}
                    </select>
                  </label>
                  <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                    <span>Country</span>
                    <select value={form.country ?? ""} onChange={(e) => handleChange("country", e.target.value)}>
                      <option value="">-- Select --</option>
                      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Size (live)</span>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                      <input type="number" step="0.01" inputMode="decimal" value={form.size_l ?? ""} onChange={(e) => handleChange("size_l", e.target.value)} style={{ flex: 1 }} />
                      <span>MW</span>
                    </div>
                  </label>

                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Size (under construction)</span>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                      <input type="number" step="0.01" inputMode="decimal" value={form.size_u ?? ""} onChange={(e) => handleChange("size_u", e.target.value)} style={{ flex: 1 }} />
                      <span>MW</span>
                    </div>
                  </label>

                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Size (design)</span>
                    <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                      <input type="number" step="0.01" inputMode="decimal" value={form.size_d ?? ""} onChange={(e) => handleChange("size_d", e.target.value)} style={{ flex: 1 }} />
                      <span>MW</span>
                    </div>
                  </label>

                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Confidence</span>
                    <select
                      value={form.confidence === "" || form.confidence == null ? "" : String(form.confidence)}
                      onChange={(e) => handleChange("confidence", e.target.value)}
                    >
                      <option value="">(blank ⇒ 0)</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </label>

                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Latitude</span>
                    <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                  </label>

                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Longitude</span>
                    <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                  </label>
                </div>
                <div className="staging-actions">
                  <button className="btn btn--primary" onClick={finishCreate} disabled={sending}>Stage</button>
                  <button className="btn btn--primary" onClick={handleStageAndAdmit} style={{ marginLeft: 8 }} disabled={sending}>Stage & Admit</button>
                  <button className="btn btn--secondary" onClick={cancelCreate} style={{ marginLeft: 8 }} disabled={sending}>Cancel</button>
                </div>
              </div>
            </InfoWindow>
          )
          }

          {/* InfoWindow for creating a new landing station (right-click) */}
          {creating && viewType === "landingstations" && (
            <InfoWindow
              position={{ lat: creating.latitude, lng: creating.longitude }}
              onCloseClick={cancelCreate}
              options={{ zIndex: 2000 }}
            >
              <div style={{ minWidth: 300 }}>
                <h5 style={{ marginBottom: 8 }}>Create staged landing station</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Name</span>
                    <input value={form.name ?? ""} onChange={(e) => handleChange("name", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Latitude</span>
                    <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Longitude</span>
                    <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                  </label>
                </div>
                <div className="staging-actions">
                  <button className="btn btn--primary" onClick={finishCreate} disabled={sending}>Stage</button>
                  <button className="btn btn--primary" onClick={handleStageAndAdmit} style={{ marginLeft: 8 }} disabled={sending}>Stage & Admit</button>
                  <button className="btn btn--secondary" onClick={cancelCreate} style={{ marginLeft: 8 }} disabled={sending}>Cancel</button>
                </div>
              </div>
            </InfoWindow>
          )}

          {/* Read-only InfoWindow for existing data centres */}
          {
            selectedCentre && (
              <InfoWindow
                position={{
                  lat: Number(selectedCentre.latitude),
                  lng: Number(selectedCentre.longitude),
                }}
                onCloseClick={() => setSelectedCentre(null)}
                options={{ zIndex: 1000 }}
              >
                <div>
                  <h5>
                    {(selectedCentre.company ? selectedCentre.company + " - " : "") +
                      (selectedCentre.sitename || selectedCentre.name)}
                  </h5>
                  <ul>
                    {selectedCentre.sitename && (
                      <li><strong>Site Name:</strong> {selectedCentre.sitename}</li>
                    )}
                    {selectedCentre.company && (
                      <li><strong>Company:</strong> {selectedCentre.company}</li>
                    )}
                    {selectedCentre.category && (
                      <li><strong>Category:</strong> {selectedCentre.category}</li>
                    )}
                    {selectedCentre.country && (
                      <li><strong>Country:</strong> {COUNTRY_MAP[selectedCentre.country] ?? selectedCentre.country}</li>
                    )}
                    {selectedCentre.size_l && (
                      <li><strong>Size (Live):</strong> {`${selectedCentre.size_l} MW`}</li>
                    )}
                    {selectedCentre.size_u && (
                      <li><strong>Size (Under Construction):</strong> {`${selectedCentre.size_u} MW`}</li>
                    )}
                    {selectedCentre.size_d && (
                      <li><strong>Size (Design):</strong> {`${selectedCentre.size_d} MW`}</li>
                    )}
                  </ul>
                </div>
              </InfoWindow>
            )
          }

          {/* Editable InfoWindow for existing centres (right-click) */}
          {
            editingCentre && (
              <InfoWindow
                position={{ lat: Number(editingCentre.latitude), lng: Number(editingCentre.longitude) }}
                onCloseClick={cancelEditCentre}
                options={{ zIndex: 1200 }}
              >
                <div style={{ minWidth: 300 }}>
                  <h5 style={{ marginBottom: 8 }}>Edit data centre</h5>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Site name</span>
                      <input value={form.sitename ?? ""} onChange={(e) => handleChange("sitename", e.target.value)} />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Company</span>
                      <input value={form.company ?? ""} onChange={(e) => handleChange("company", e.target.value)} />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Category</span>
                      <select value={form.category ?? ""} onChange={(e) => handleChange("category", e.target.value)}>
                        <option value="">-- Select --</option>
                        <option value="Colocation">Colocation</option>
                        <option value="Self-build">Self-build</option>
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Subcategory</span>
                      <select value={form.subcategory ?? ""} onChange={(e) => handleChange("subcategory", e.target.value)}>
                        <option value="">-- Select --</option>
                        {SUBCATEGORIES.map((sub, idx) => <option key={idx} value={sub}>{sub}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Country</span>
                      <select value={form.country ?? ""} onChange={(e) => handleChange("country", e.target.value)}>
                        <option value="">-- Select --</option>
                        {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (live)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_l ?? ""} onChange={(e) => handleChange("size_l", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (under construction)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_u ?? ""} onChange={(e) => handleChange("size_u", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (design)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_d ?? ""} onChange={(e) => handleChange("size_d", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Confidence</span>
                      <select
                        value={form.confidence === "" || form.confidence == null ? "" : String(form.confidence)}
                        onChange={(e) => handleChange("confidence", e.target.value)}
                      >
                        <option value="">(blank ⇒ 0)</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Latitude</span>
                      <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                    </label>
                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Longitude</span>
                      <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                    </label>
                  </div>
                  <div className="staging-actions">
                    <button className="btn btn--primary" onClick={saveEditedCentre} disabled={sending}>Save</button>
                    <button className="btn btn--secondary" onClick={cancelEditCentre} style={{ marginLeft: 8 }}>Cancel</button>
                  </div>
                </div>
              </InfoWindow>
            )
          }

          {/* Read-only InfoWindow for existing landing stations */}
          {selectedStation && !editingStation && (
            <InfoWindow
              position={{ lat: Number(selectedStation.latitude), lng: Number(selectedStation.longitude) }}
              onCloseClick={() => setSelectedStation(null)}
              options={{ zIndex: 1000 }}
            >
              <div>
                <h5>{selectedStation.name || "Landing Station"}</h5>
                <ul>
                  {selectedStation.name && <li><strong>Name:</strong> {selectedStation.name}</li>}
                  <li><strong>Latitude:</strong> {selectedStation.latitude}</li>
                  <li><strong>Longitude:</strong> {selectedStation.longitude}</li>
                  {selectedStation.cables && (
                    <li>
                      <strong>Cables:</strong>
                      <ul>
                        {selectedStation.cables
                          .split(',')
                          .map((c) => c.trim())
                          .filter(Boolean)
                          .map((cable, idx) => (
                            <li key={idx}>{cable}</li>
                          ))}
                      </ul>
                    </li>
                  )}
                </ul>
              </div>
            </InfoWindow>
          )}

          {/* Editable InfoWindow for existing landing stations (right-click) */}
          {editingStation && (
            <InfoWindow
              position={{ lat: Number(editingStation.latitude), lng: Number(editingStation.longitude) }}
              onCloseClick={cancelEditStation}
              options={{ zIndex: 1200 }}
            >
              <div style={{ minWidth: 300 }}>
                <h5 style={{ marginBottom: 8 }}>Edit landing station</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Name</span>
                    <input value={form.name ?? ""} onChange={(e) => handleChange("name", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Latitude</span>
                    <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Longitude</span>
                    <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                  </label>
                </div>
                <div className="staging-actions">
                  <button className="btn btn--primary" onClick={saveEditedStation} disabled={sending}>Save</button>
                  <button className="btn btn--secondary" onClick={cancelEditStation} style={{ marginLeft: 8 }}>Cancel</button>
                </div>
              </div>
            </InfoWindow>
          )}

          {/* Editable InfoWindow for staged landing stations (right-click) */}
          {selectedStation && !editingStation && stagedStations.find(s => s.staging_id === selectedStation) && (
            <InfoWindow
              position={{ lat: Number(form.latitude), lng: Number(form.longitude) }}
              onCloseClick={() => { setSelectedStation(null); setForm({}); }}
              options={{ zIndex: 1000 }}
            >
              <div style={{ minWidth: 300 }}>
                <h5 style={{ marginBottom: 8 }}>Staged landing station</h5>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Name</span>
                    <input value={form.name ?? ""} onChange={(e) => handleChange("name", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Latitude</span>
                    <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                  </label>
                  <label style={{ display: "flex", flexDirection: "column" }}>
                    <span>Longitude</span>
                    <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                  </label>
                </div>
                <div className="staging-actions">
                  <button className="btn btn--primary" onClick={handleAdmit} disabled={sending}>Admit (insert)</button>
                  <button className="btn btn--secondary" onClick={handleReject}>Reject (discard)</button>
                  <button className="btn btn--secondary" onClick={() => { setSelectedStation(null); setForm({}); }} style={{ marginLeft: 8 }}>Cancel</button>
                </div>
              </div>
            </InfoWindow>
          )}

          {/* Editable InfoWindow only for staged points */}
          {
            selected && (
              <InfoWindow
                position={{ lat: Number(form.latitude), lng: Number(form.longitude) }}
                // Keep InfoWindow open on outside clicks; user must click Cancel to close
                onCloseClick={() => { /* no-op */ }}
                options={{ zIndex: 1000 }}
              >
                <div style={{ minWidth: 300 }}>
                  <h5 style={{ marginBottom: 8 }}>Staged point</h5>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Site name</span>
                      <input value={form.sitename ?? ""} onChange={(e) => handleChange("sitename", e.target.value)} />
                    </label>

                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Company</span>
                      <input value={form.company ?? ""} onChange={(e) => handleChange("company", e.target.value)} />
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Category</span>
                      <select value={form.category ?? ""} onChange={(e) => handleChange("category", e.target.value)}>
                        <option value="">-- Select --</option>
                        <option value="Colocation">Colocation</option>
                        <option value="Self-build">Self-build</option>
                      </select>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Subcategory</span>
                      <select value={form.subcategory ?? ""} onChange={(e) => handleChange("subcategory", e.target.value)}>
                        <option value="">-- Select --</option>
                        {SUBCATEGORIES.map((sub, idx) => <option key={idx} value={sub}>{sub}</option>)}
                      </select>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column", gridColumn: "1 / span 2" }}>
                      <span>Country</span>
                      <select value={form.country ?? ""} onChange={(e) => handleChange("country", e.target.value)}>
                        <option value="">-- Select --</option>
                        {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                      </select>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (live)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_l ?? ""} onChange={(e) => handleChange("size_l", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (under construction)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_u ?? ""} onChange={(e) => handleChange("size_u", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Size (design)</span>
                      <div style={{ display: "flex", flexDirection: "row", gap: "5px", justifyContent: "space-between", alignItems: "center" }}>
                        <input type="number" step="0.01" inputMode="decimal" value={form.size_d ?? ""} onChange={(e) => handleChange("size_d", e.target.value)} style={{ flex: 1 }} />
                        <span>MW</span>
                      </div>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Confidence</span>
                      <select
                        value={form.confidence === "" || form.confidence == null ? "" : String(form.confidence)}
                        onChange={(e) => handleChange("confidence", e.target.value)}
                      >
                        <option value="">(blank ⇒ 0)</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                      </select>
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Latitude</span>
                      <input inputMode="decimal" value={form.latitude ?? ""} onChange={(e) => handleChange("latitude", e.target.value)} />
                    </label>

                    <label style={{ display: "flex", flexDirection: "column" }}>
                      <span>Longitude</span>
                      <input inputMode="decimal" value={form.longitude ?? ""} onChange={(e) => handleChange("longitude", e.target.value)} />
                    </label>
                  </div>

                  <div className="staging-actions">
                    <button className="btn btn--primary" onClick={handleAdmit} disabled={sending}>Admit (insert)</button>
                    <button className="btn btn--secondary" onClick={handleReject}>Reject (discard)</button>
                    <button className="btn btn--secondary" onClick={deselect} style={{ marginLeft: 8 }}>Cancel</button>
                  </div>
                </div>
              </InfoWindow>
            )
          }
        </GoogleMap >
      )}
    </div >
  );
};

export default StagingInterface;
